import type {
  ExtendedListing,
  FetchByMaloneLotIdEncodedParams,
  FetchByMaloneLotIdEncodedResponse,
  ListingAreaSet,
} from '@/src/lib/api/endpoints/listing';
import type { PropertyDetailListing } from '@/src/components/features/property/types';
import type { ListingMinMaxPriceBeds, Photo } from '@/src/types/commonTypes.type';
import { normalizeAmenities } from './normalizeAmenities';
import { capListingPhotos } from './listingTransforms';
import { getListingHdpHref, normalizeListingHref } from './listingHref';
import { deriveModelSummaryFromListing } from './listingModelSummary';
import {
  type AmenityHighlight,
  buildLegacyAmenitiesPreview,
} from '@/src/components/features/property/AmenitiesModule/amenitiesPreview';

export const LEGACY_BUILDING_DETAIL_PARAMS = {
  active: true,
  isBuilding: true,
  type: 'building',
} satisfies FetchByMaloneLotIdEncodedParams;

type LegacyBuildingDetailOptions = {
  requestedMaloneLotIdEncoded: string;
};

export type BuildingInventory = {
  totalUnitCount?: number;
  availableUnitCount?: number;
};

type LegacyBuildingDetailData = {
  listing: PropertyDetailListing;
  units: Array<PropertyDetailListing>;
  buildingInventory: BuildingInventory;
  maloneLotIdEncoded: string;
  geo: { lat: number; lon: number } | null;
  areas: ListingAreaSet;
  unitRedirectHref: string | null;
};

type AreaBreadcrumb = NonNullable<ListingAreaSet['specific']>;

const DEFAULT_SCHOOL_SEARCH_SLUG = 'apartments-for-rent';

function getSearchSlugFromListingPropertyType(propertyType: unknown): string {
  switch (String(propertyType ?? '').toLowerCase()) {
    case 'house':
    case 'townhouse':
      return 'houses-for-rent';
    case 'condo':
      return 'condos-for-rent';
    default:
      return DEFAULT_SCHOOL_SEARCH_SLUG;
  }
}

function asNumber(value: unknown): number | undefined {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value !== 'string' || value.trim() === '') return undefined;
  const numberValue = Number(value);
  return Number.isFinite(numberValue) ? numberValue : undefined;
}

function numberOrZero(value: unknown): number {
  return asNumber(value) ?? 0;
}

function stringValue(value: unknown): string {
  return typeof value === 'string' || typeof value === 'number' ? String(value) : '';
}

function positiveWholeNumber(value: unknown): number | undefined {
  const numberValue = asNumber(value);
  return numberValue && Number.isSafeInteger(numberValue) && numberValue > 0 ? numberValue : undefined;
}

function normalizeArea(area: AreaBreadcrumb | undefined): AreaBreadcrumb | undefined {
  if (!area) return undefined;
  return {
    ...area,
    uriV2: area.uriV2 ?? area.uri,
  };
}

function normalizeAreas(areas: ListingAreaSet | undefined): ListingAreaSet {
  return {
    specific: normalizeArea(areas?.specific),
    breadcrumbs: areas?.breadcrumbs?.map(normalizeArea).filter((area): area is AreaBreadcrumb => Boolean(area)),
    links: areas?.links?.map(normalizeArea).filter((area): area is AreaBreadcrumb => Boolean(area)),
    districts: areas?.districts?.map(normalizeArea).filter((area): area is AreaBreadcrumb => Boolean(area)),
    schools: areas?.schools,
  };
}

function normalizeSchools(
  schools: FetchByMaloneLotIdEncodedResponse['schools'],
  searchSlug: string,
): PropertyDetailListing['schools'] {
  return (schools ?? []).map((school) => ({
    ...school,
    id: school.id ?? school.areaSid ?? String(school.areaId ?? school.resourceId ?? ''),
    name: school.name ?? '',
    resourceId: school.resourceId ?? '',
    type: school.type ?? '',
    isPrivate: Boolean(school.isPrivate),
    lowGrade: school.lowGrade ?? '',
    highGrade: school.highGrade ?? '',
    rating: numberOrZero(school.rating),
    geo: {
      lat: numberOrZero(school.geo?.lat),
      lon: numberOrZero(school.geo?.lon),
    },
    uriV2: school.uriV2 ?? (school.resourceId ? `/${school.resourceId}/${searchSlug}` : ''),
  })) as PropertyDetailListing['schools'];
}

function listingHref(listing: ExtendedListing): string {
  return normalizeListingHref(listing.uriMalone ?? listing.uriV2 ?? (listing as { hdpUrl?: string }).hdpUrl) ?? '';
}

function buildingHrefFromListing(listing: ExtendedListing): string {
  const direct = normalizeListingHref(listing.uriBuilding ?? listing.buildingHref);
  if (direct) return direct;

  const href = listingHref(listing);
  const [path] = href.split('?');
  const [slug] = path.split('/').filter(Boolean);
  return slug ? `/${slug}/building` : '';
}

function unitNameFromHref(href: string): string {
  const [path] = href.split('?');
  const segments = path.split('/').filter(Boolean);
  return segments.length === 3 && segments[2] === 'pad' ? decodeURIComponent(segments[1] ?? '') : '';
}

type RawModelPricing = { numBeds?: number; lowPrice?: number; highPrice?: number };

function rawModelsAndPricing(listing: ExtendedListing): Array<RawModelPricing> {
  const rawModels = (listing as { models?: unknown }).models;
  if (!Array.isArray(rawModels)) return [];

  return rawModels.flatMap((model) => {
    if (typeof model !== 'object' || model === null) return [];
    const rawModel = model as { beds?: unknown; low?: unknown; high?: unknown };
    const numBeds = asNumber(rawModel.beds);
    const lowPrice = asNumber(rawModel.low);
    const highPrice = asNumber(rawModel.high);
    return numBeds === undefined && lowPrice === undefined && highPrice === undefined
      ? []
      : [{ ...(numBeds !== undefined ? { numBeds } : {}), ...(lowPrice !== undefined ? { lowPrice } : {}), ...(highPrice !== undefined ? { highPrice } : {}) }];
  });
}

function normalizeUnit(
  rawListing: ExtendedListing,
  context: {
    areas: ListingAreaSet;
    geo: { lat: number; lon: number } | null;
    hasMultipleUnits: boolean;
    requestedMaloneLotIdEncoded: string;
  },
): PropertyDetailListing {
  const capped = capListingPhotos(rawListing);
  const href = listingHref(capped);
  const uriBuilding = buildingHrefFromListing(capped);
  const unitName = stringValue(capped.unit || (capped as { urlMaloneUnit?: string }).urlMaloneUnit) || unitNameFromHref(href);
  const lotIdEncoded = stringValue(capped.lotIdEncoded ?? capped.maloneLotIdEncoded) || context.requestedMaloneLotIdEncoded;
  const medPhotoUrls = capped.medPhotoUrls?.length
    ? capped.medPhotoUrls
    : capped.medPhotoUrl
      ? [capped.medPhotoUrl]
      : [];
  const amenities = normalizeAmenities(capped.amenities as unknown as Record<string, unknown> | undefined);
  const highlights = (amenities as { highlights?: Array<AmenityHighlight> } | null | undefined)?.highlights ?? [];
  const modelsAndPricing = rawModelsAndPricing(capped);

  return {
    ...capped,
    active: capped.active !== false,
    amenities: (amenities ?? capped.amenities) as PropertyDetailListing['amenities'],
    amenitiesPreview: buildLegacyAmenitiesPreview(highlights),
    areas: undefined,
    belongsToMultipleUnitBuilding: context.hasMultipleUnits,
    building: false,
    geo: capped.geo ?? context.geo ?? undefined,
    hdpUrl: href,
    isUnit: true,
    lotIdEncoded,
    maloneLotIdEncoded: lotIdEncoded,
    medPhotoUrls,
    modelSummary: deriveModelSummaryFromListing(capped as Record<string, unknown>) ?? capped.modelSummary,
    modelsAndPricing: modelsAndPricing.length > 0 ? modelsAndPricing : undefined,
    schools: undefined,
    unit: unitName,
    uriBuilding,
    uriMalone: href || capped.uriMalone,
    uriV2: capped.uriV2 ?? href,
    urlMaloneUnit: unitName,
  } as PropertyDetailListing;
}

function minDefined(a: number | undefined, b: number | undefined): number | undefined {
  if (a === undefined) return b;
  if (b === undefined) return a;
  return Math.min(a, b);
}

function maxDefined(a: number | undefined, b: number | undefined): number | undefined {
  if (a === undefined) return b;
  if (b === undefined) return a;
  return Math.max(a, b);
}

/**
 * Groups the units' model entries by bedroom count with summed quantities,
 * mirroring the v3 adapter's `buildModelsAndPricing` so quantity-driven
 * consumers (the FAQ availability question) work on building pages.
 */
function aggregateModelsAndPricing(
  units: Array<PropertyDetailListing>,
): Array<RawModelPricing & { quantity: number }> {
  const modelsByBeds = new Map<number, RawModelPricing & { numBeds: number; quantity: number }>();
  for (const model of units.flatMap((unit) => (unit.modelsAndPricing ?? []) as Array<RawModelPricing>)) {
    const numBeds = model.numBeds ?? 0;
    const current = modelsByBeds.get(numBeds);
    if (!current) {
      modelsByBeds.set(numBeds, { numBeds, lowPrice: model.lowPrice, highPrice: model.highPrice, quantity: 1 });
      continue;
    }
    current.quantity += 1;
    current.lowPrice = minDefined(current.lowPrice, model.lowPrice);
    current.highPrice = maxDefined(current.highPrice, model.highPrice);
  }
  return [...modelsByBeds.values()].sort((a, b) => a.numBeds - b.numBeds);
}

function firstPhoto(unit: PropertyDetailListing, key: 'photos' | 'largePhotos' | 'thumbPhotos'): Photo | null {
  return unit[key]?.[0] ?? null;
}

function collectFirstPhotos(
  units: Array<PropertyDetailListing>,
  key: 'photos' | 'largePhotos' | 'thumbPhotos',
): Array<Photo> {
  const seen = new Set<string>();
  return units
    .map((unit) => firstPhoto(unit, key))
    .filter((photo): photo is Photo => Boolean(photo?.url))
    .filter((photo) => {
      if (seen.has(photo.url)) return false;
      seen.add(photo.url);
      return true;
    });
}

function rangeValues(units: Array<PropertyDetailListing>, keys: Array<keyof ListingMinMaxPriceBeds>): Array<number> {
  return units.flatMap((unit) =>
    keys
      .map((key) => asNumber(unit.listingMinMaxPriceBeds?.[key] ?? (unit.modelSummary as Record<string, unknown> | undefined)?.[key]))
      .filter((value): value is number => value !== undefined),
  );
}

function minMax(values: Array<number>): { min: number; max: number } | null {
  if (values.length === 0) return null;
  return { min: Math.min(...values), max: Math.max(...values) };
}

function summarizeUnits(units: Array<PropertyDetailListing>): ListingMinMaxPriceBeds | undefined {
  const price = minMax(rangeValues(units, ['minPrice', 'maxPrice']));
  const beds = minMax(rangeValues(units, ['minBeds', 'maxBeds']));
  const baths = minMax(rangeValues(units, ['minBaths', 'maxBaths']));
  const sqft = minMax(rangeValues(units, ['minSqft', 'maxSqft']));
  if (!price && !beds && !baths && !sqft) return undefined;

  return {
    DATA_MODEL: 'ListingMinMaxPriceBeds',
    minPrice: price?.min ?? 0,
    maxPrice: price?.max ?? price?.min ?? 0,
    minBeds: beds?.min ?? 0,
    maxBeds: beds?.max ?? beds?.min ?? 0,
    minBaths: baths?.min ?? 0,
    maxBaths: baths?.max ?? baths?.min ?? 0,
    minSqft: sqft?.min ?? 0,
    maxSqft: sqft?.max ?? sqft?.min ?? 0,
    bedDisplay: '',
    bedDisplayAllAvailable: '',
    bathDisplay: '',
    priceDisplay: '',
    priceDisplayRange: '',
    sqftDisplay: '',
    bedDisplayCompact: '',
    bedDisplaySemiCompact: '',
    bathDisplayCompact: '',
    bathDisplaySemiCompact: '',
  };
}

function buildingDisplayName(firstUnit: PropertyDetailListing): string {
  if (firstUnit.address?.hideStreet) return firstUnit.displayName || firstUnit.title || '';
  return firstUnit.address?.street || firstUnit.displayName || firstUnit.title || 'Building';
}

function buildingPageTitle(firstUnit: PropertyDetailListing): string {
  const address = firstUnit.address;
  return [address?.street, address?.city, address?.state, address?.zip].filter(Boolean).join(', ');
}

function availableRentalUnits(response: FetchByMaloneLotIdEncodedResponse): Array<string> {
  return (response.availableUnits?.rental ?? []).map(String).filter(Boolean);
}

function getBuildingInventory(
  response: FetchByMaloneLotIdEncodedResponse,
  rawListings: Array<ExtendedListing>,
): BuildingInventory {
  const rawAvailableUnits = response.availableUnits?.rental;
  const availableUnitCount = Array.isArray(rawAvailableUnits)
    ? new Set(rawAvailableUnits.map(stringValue).filter(Boolean)).size
    : undefined;
  const totalUnitCounts = new Set(
    rawListings
      .map((listing) => positiveWholeNumber(listing.details?.numUnits))
      .filter((count): count is number => count !== undefined),
  );
  // Each unit response normally carries the same building total. When the API
  // disagrees, leave the total out rather than selecting an arbitrary unit.
  const totalUnitCount = totalUnitCounts.size === 1 ? [...totalUnitCounts][0] : undefined;

  return {
    ...(totalUnitCount !== undefined ? { totalUnitCount } : {}),
    ...(availableUnitCount !== undefined ? { availableUnitCount } : {}),
  };
}

function buildBuildingListing(input: {
  areas: ListingAreaSet;
  geo: { lat: number; lon: number } | null;
  response: FetchByMaloneLotIdEncodedResponse;
  schools: PropertyDetailListing['schools'];
  units: Array<PropertyDetailListing>;
  requestedMaloneLotIdEncoded: string;
}): PropertyDetailListing {
  const { areas, geo, response, schools, units, requestedMaloneLotIdEncoded } = input;
  const firstUnit = units[0];
  const hasMultipleUnits = units.length > 1;
  const maloneLotIdEncoded = response.lotIdEncoded ?? requestedMaloneLotIdEncoded;
  const uriBuilding = firstUnit.uriBuilding || buildingHrefFromListing(firstUnit as ExtendedListing);
  const photos = collectFirstPhotos(units, 'photos');
  const largePhotos = collectFirstPhotos(units, 'largePhotos');
  const thumbPhotos = collectFirstPhotos(units, 'thumbPhotos');
  const listingMinMaxPriceBeds = summarizeUnits(units) ?? firstUnit.listingMinMaxPriceBeds;
  const modelsAndPricing = aggregateModelsAndPricing(units);

  return {
    ...firstUnit,
    active: units.every((unit) => unit.active !== false),
    areas,
    belongsToMultipleUnitBuilding: hasMultipleUnits,
    building: true,
    details: {
      ...firstUnit.details,
      numUnits: units.length,
      title: buildingPageTitle(firstUnit),
    } as PropertyDetailListing['details'],
    displayName: buildingDisplayName(firstUnit),
    forRentUnitsInBuilding: availableRentalUnits(response),
    geo: geo ?? firstUnit.geo,
    hdpUrl: uriBuilding,
    isBuilding: true,
    isUnit: false,
    largePhotos: largePhotos.length ? largePhotos : firstUnit.largePhotos,
    listingMinMaxPriceBeds,
    lotIdEncoded: maloneLotIdEncoded,
    maloneLotIdEncoded,
    modelsAndPricing: modelsAndPricing.length > 0 ? modelsAndPricing : firstUnit.modelsAndPricing,
    photos: photos.length ? photos : firstUnit.photos,
    previewPhoto: largePhotos[0] ?? firstUnit.previewPhoto,
    schools,
    searchKeyword: hasMultipleUnits ? `${units.length} Units Available` : firstUnit.searchKeyword,
    thumbPhotos: thumbPhotos.length ? thumbPhotos : firstUnit.thumbPhotos,
    // Legacy payloads carry trusted on details, not the top level; hoist it like
    // the v3 adapter so listing-level gates (e.g. the FAQ module) can read it.
    trusted: Boolean((firstUnit.details as { trusted?: boolean } | undefined)?.trusted),
    uriBuilding,
    uriV2: hasMultipleUnits ? uriBuilding : firstUnit.uriMalone,
  } as unknown as PropertyDetailListing;
}

export function adaptLegacyBuildingDetail(
  response: FetchByMaloneLotIdEncodedResponse,
  options: LegacyBuildingDetailOptions,
): LegacyBuildingDetailData | null {
  const rawListings = response.listings ?? (response.listing ? [response.listing] : []);
  if (rawListings.length === 0) return null;

  const buildingInventory = getBuildingInventory(response, rawListings);
  const rawGeo = response.geo ?? null;
  const geo = rawGeo && typeof rawGeo.lat === 'number' && typeof rawGeo.lon === 'number'
    ? { lat: rawGeo.lat, lon: rawGeo.lon }
    : null;
  const areas = normalizeAreas(response.areas);
  const schools = normalizeSchools(response.schools, getSearchSlugFromListingPropertyType(rawListings[0]?.propertyType));
  const hasMultipleUnits = rawListings.length > 1;
  const units = rawListings.map((rawListing) =>
    normalizeUnit(rawListing, {
      areas,
      geo,
      hasMultipleUnits,
      requestedMaloneLotIdEncoded: options.requestedMaloneLotIdEncoded,
    }),
  );
  const listing = buildBuildingListing({
    areas,
    geo,
    response,
    schools,
    units,
    requestedMaloneLotIdEncoded: options.requestedMaloneLotIdEncoded,
  });

  return {
    listing,
    units,
    buildingInventory,
    maloneLotIdEncoded: response.lotIdEncoded ?? options.requestedMaloneLotIdEncoded,
    geo,
    areas,
    unitRedirectHref: units.length === 1 ? getListingHdpHref(units[0]) : null,
  };
}
